/**
 * Dadware shell — dw-shell.js (deferred). Announcement rotation, cart badge
 * feedback, and express-checkout re-initialisation after the cart drawer
 * re-renders. No libraries; everything is optional enhancement.
 */
(() => {
  'use strict';

  /* ------------------------------------------------ announcement bar */
  if (!customElements.get('dw-announcement')) {
    class DwAnnouncement extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('.dw-announce__item')];
        this.toggleBtn = this.querySelector('[data-announce-toggle]');
        this.index = 0;
        this.hover = false;
        this.userPaused = false;
        this.mqMobile = window.matchMedia('(max-width: 749px)');
        this.mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onToggle = () => {
          this.userPaused = !this.userPaused;
          this.toggleBtn.setAttribute('aria-pressed', String(this.userPaused));
          this.toggleBtn.setAttribute('aria-label', this.userPaused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause);
          this.toggleBtn.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', this.userPaused);
          this.toggleBtn.querySelector('[data-icon-play]')?.toggleAttribute('hidden', !this.userPaused);
          this.sync();
        };
        this.onEnter = () => { this.hover = true; this.sync(); };
        this.onLeave = () => { this.hover = false; this.sync(); };
        this.sync = this.sync.bind(this);

        this.toggleBtn?.addEventListener('click', this.onToggle);
        this.addEventListener('pointerenter', this.onEnter);
        this.addEventListener('pointerleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
        document.addEventListener('visibilitychange', this.sync);
        this.mqMobile.addEventListener?.('change', this.sync);
        this.mqReduced.addEventListener?.('change', this.sync);
        this.sync();
      }

      canRotate() {
        return (
          this.hasAttribute('data-rotate') &&
          this.items.length > 1 &&
          this.mqMobile.matches &&
          !this.mqReduced.matches &&
          !this.userPaused &&
          !this.hover &&
          !document.hidden
        );
      }

      sync() {
        clearInterval(this.timer);
        this.timer = null;
        if (this.canRotate()) this.timer = setInterval(() => this.next(), Number(this.dataset.interval) || 6000);
      }

      next() {
        this.items[this.index]?.classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index]?.classList.add('is-active');
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        this.toggleBtn?.removeEventListener('click', this.onToggle);
        document.removeEventListener('visibilitychange', this.sync);
        this.mqMobile.removeEventListener?.('change', this.sync);
        this.mqReduced.removeEventListener?.('change', this.sync);
      }
    }
    customElements.define('dw-announcement', DwAnnouncement);
  }

  /* ------------------------------------------ cart badge + express buttons */
  const bump = () => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    bubble.classList.remove('is-bumped');
    void bubble.offsetWidth;
    bubble.classList.add('is-bumped');
    setTimeout(() => bubble.classList.remove('is-bumped'), 600);
  };

  let expressTimer = null;
  const initExpress = () => {
    clearTimeout(expressTimer);
    expressTimer = setTimeout(() => {
      try {
        window.Shopify?.StorefrontExpressButtons?.initialize?.();
      } catch (e) {
        /* express buttons are optional */
      }
    }, 80);
  };

  const drawer = document.querySelector('cart-drawer');
  if (drawer && 'MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if (mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('.dw-drawer__express, .cart-drawer__footer') || n.querySelector?.('.dw-drawer__express'))))) {
        initExpress();
      }
    }).observe(drawer, { childList: true, subtree: true });
  }

  const onCart = (event) => {
    if (event && event.source === 'cart-items') return;
    bump();
  };

  /* ------------------------------------------- gift finder (custom analytics) */
  document.addEventListener('click', (event) => {
    const card = event.target.closest('[data-dw-gift-card]');
    if (!card || !window.DW || typeof window.DW.track !== 'function') return;
    window.DW.track('gift_finder_click', { recipient: card.dataset.recipient || '', destination: card.getAttribute('href') || '' });
  });

  /* --------------------------------------------- email signup (inline, no alerts) */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-dw-email]');
    if (!form) return;
    const input = form.querySelector('input[type="email"]');
    const msg = form.querySelector('[data-dw-email-msg]');
    const button = form.querySelector('button[type="submit"]');
    if (!input) return;
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value.trim());
    if (!valid) {
      event.preventDefault();
      input.setAttribute('aria-invalid', 'true');
      if (msg) {
        msg.textContent = msg.dataset.invalid || '';
        msg.classList.add('is-error');
      }
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');
    if (msg) msg.textContent = '';
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }
  });

  const focusEmailResult = () => {
    const result = document.querySelector('[data-dw-email-success]') || document.querySelector('.dw-email__msg.is-error');
    if (!result) return;
    result.scrollIntoView({ block: 'center' });
    if (result.hasAttribute('tabindex')) result.focus({ preventScroll: true });
  };
  if (/[?&]customer_posted=true/.test(window.location.search) || document.querySelector('.dw-email__msg.is-error')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', focusEmailResult, { once: true });
    else focusEmailResult();
  }

  const wire = () => {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, onCart);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire, { once: true });
  else wire();
})();
